import type { ConceptionPath, CountryRule, RecommendedPathway, TimelinePhase, UserCategory } from './types';

export const categoryLabels: Record<UserCategory, { title: string; en: string; copy: string }> = {
  spouses: {
    title: '配偶与异性伴侣',
    en: 'Married Couples & Spouses',
    copy: '寻求常规试管婴儿（IVF）周期、胚胎植入前基因检测（PGT），或妊娠代孕途径的合法伴侣。'
  },
  single_woman: {
    title: '单身女性传承者',
    en: 'Single Intending Mother',
    copy: '独立女性探索低温捐精授精、冷冻卵子复活取卵、亲自怀孕，或代孕助孕途径。'
  },
  single_man: {
    title: '单身男性传承者',
    en: 'Single Intending Father',
    copy: '独立男性利用登记合格的爱心捐卵与妊娠代孕安排，规划安全合规的父亲路径。'
  },
  female_couple: {
    title: '双女伴侣',
    en: 'Female Couple / ROPA Path',
    copy: '女性伴侣探索双母试管（ROPA）、捐精、伴侣怀孕或亲自怀孕的合规家庭路径。'
  },
  male_couple: {
    title: '双男伴侣',
    en: 'Male Couple / Surrogacy Path',
    copy: '男性伴侣通常需结合供卵、精子来源确认、妊娠代孕与出生亲权法律框架。'
  },
  other_lgbtq: {
    title: '其他 / 跨性别 / 未分类',
    en: 'Other LGBTQ+ / Individual Review',
    copy: '跨性别、非二元或未分类家庭情境，需由顾问先厘清配子、承载与法律身份。'
  }
};

export const pathLabels: Record<ConceptionPath, { title: string; en: string; copy: string }> = {
  artificial_insemination: {
    title: '人工授精（IUI）',
    en: 'Intrauterine Insemination',
    copy: '侵入性较低，适合部分排卵窗口与精液条件稳定的初阶方案。'
  },
  ivf: {
    title: 'IVF（常规体外受精 / 试管婴儿）',
    en: 'Classical In-Vitro Fertilization',
    copy: '促排取卵、体外受精与胚胎培养，整体临床成功率与可控性最高。'
  },
  egg_donation: {
    title: '第三方卵子供应',
    en: 'Oocyte Donor Matching',
    copy: '适用于高龄、低储备、反复胚胎品质不佳，或需提高胚胎来源稳定度的家庭。'
  },
  sperm_donation: {
    title: '第三方精子供应',
    en: 'Sperm Donor Matching',
    copy: '适用于单身女性、女同性伴侣、严重男性因素或需认证低温精子库的配置。'
  },
  surrogacy: {
    title: '妊娠代孕合作',
    en: 'Gestational Surrogacy',
    copy: '涉及孕母匹配、法律公证、信托托管与出生亲权确立，需逐国审慎核对。'
  },
  donor_embryo: {
    title: '胚胎领养 / 捐胚',
    en: 'Donor Embryo',
    copy: '以已形成胚胎为起点，重点在来源合规、遗传资讯透明与亲权文件。'
  },
  reciprocal_ivf: {
    title: '双母试管',
    en: 'Reciprocal IVF',
    copy: '一方提供卵子形成胚胎，另一方怀孕，需同步核对当地法律与医疗准入。'
  }
};

export const pathwayLabels: Record<RecommendedPathway, { title: string; en: string; copy: string }> = {
  iui_or_ivf: {
    title: 'IUI / IVF 初阶至进阶路径',
    en: 'IUI or IVF Candidate',
    copy: '配子与承载条件相对完整，可由医师根据年龄、输卵管、精液与时间成本决定 IUI 或 IVF。'
  },
  ivf: {
    title: '常规 IVF 试管婴儿路径',
    en: 'Classical IVF',
    copy: '以促排取卵、体外受精、胚胎培养与移植为主轴，适合需要较高可控性的家庭。'
  },
  icsi_ivf: {
    title: 'ICSI / IVF 精准受精路径',
    en: 'ICSI IVF',
    copy: '当精子条件待确认或偏弱时，优先由胚胎实验室评估单精子注射与精液优化。'
  },
  ropa: {
    title: '双母试管 ROPA 路径',
    en: 'Reciprocal IVF / ROPA',
    copy: '一方提供卵子，另一方承载妊娠，通常需结合捐精与所在地多元家庭法律核对。'
  },
  donor_egg_ivf: {
    title: '供卵 IVF 路径',
    en: 'Donor Egg IVF',
    copy: '以第三方卵子建立胚胎，重点在供卵者筛查、配型、法律文件与胚胎实验室品质。'
  },
  embryo_transfer: {
    title: '既有胚胎移植 / 转运路径',
    en: 'Embryo Transfer / Transport',
    copy: '以既有冷冻胚胎为起点，核心在胚胎资料、PGT 状态、冷冻地点与跨境转运可行性。'
  },
  surrogacy_ivf: {
    title: '代孕 IVF 亲权路径',
    en: 'Surrogacy IVF',
    copy: '需要供卵或自卵、自精或捐精、孕母匹配、托管信托、法律公证与出生亲权确立。'
  },
  needs_review: {
    title: '需顾问个案评估',
    en: 'Consultant Review Required',
    copy: '目前资源、承载或身份设定存在不确定性，建议先由顾问厘清再形成确定方案。'
  }
};

export const countries: CountryRule[] = [
  {
    code: 'US',
    country: '美国',
    enName: 'USA',
    flag: '🇺🇸',
    allowedCategories: ['spouses', 'single_woman', 'single_man', 'female_couple', 'male_couple', 'other_lgbtq'],
    surrogacyAllowed: true,
    genderSelectionAllowed: true,
    eggDonationAllowed: true,
    spermDonationAllowed: true,
    averageCostUSD: 145000,
    quality: 5,
    summary: '法律保障的最高标准。不论婚姻状况与性倾向，多数友善州可提供完整亲权路径。',
    legalNote: '产前家长权利令与托管信托通常可直接确立亲权，州别差异仍需律师核对。'
  },
  {
    code: 'CO',
    country: '哥伦比亚',
    enName: 'Colombia',
    flag: '🇨🇴',
    allowedCategories: ['spouses', 'single_woman', 'single_man', 'female_couple', 'male_couple', 'other_lgbtq'],
    surrogacyAllowed: true,
    genderSelectionAllowed: false,
    eggDonationAllowed: true,
    spermDonationAllowed: true,
    averageCostUSD: 64000,
    quality: 4,
    summary: '得益于反歧视法制，对 LGBTQ+ 与单身男性相对友善，成本低于美国。',
    legalNote: '出生后登记与亲权文件流程需提前由当地律师安排。'
  },
  {
    code: 'GE',
    country: '乔治亚',
    enName: 'Georgia',
    flag: '🇬🇪',
    allowedCategories: ['spouses'],
    surrogacyAllowed: true,
    genderSelectionAllowed: false,
    eggDonationAllowed: true,
    spermDonationAllowed: false,
    averageCostUSD: 45000,
    quality: 4,
    summary: '成本与医疗成熟度具吸引力，但准入门槛高，主要限异性已婚夫妻。',
    legalNote: '单身与 LGBTQ+ 路径通常不适用，不应作为泛用方案推荐。'
  },
  {
    code: 'GR',
    country: '希腊',
    enName: 'Greece',
    flag: '🇬🇷',
    allowedCategories: ['spouses', 'single_woman', 'female_couple'],
    surrogacyAllowed: true,
    genderSelectionAllowed: false,
    eggDonationAllowed: true,
    spermDonationAllowed: true,
    averageCostUSD: 75000,
    quality: 4,
    summary: '欧洲框架完善，单身女性与异性已婚伴侣可在法院批案后进行。',
    legalNote: '移植前法院核准是关键节点，LGBTQ+ 伴侣目前限制较多。'
  },
  {
    code: 'MX',
    country: '墨西哥',
    enName: 'Mexico',
    flag: '🇲🇽',
    allowedCategories: ['spouses', 'single_woman', 'single_man', 'female_couple', 'male_couple', 'other_lgbtq'],
    surrogacyAllowed: true,
    genderSelectionAllowed: true,
    eggDonationAllowed: true,
    spermDonationAllowed: true,
    averageCostUSD: 68000,
    quality: 4,
    summary: '美国邻近且成本较低，需挑选司法保护路线成熟的州与诊所。',
    legalNote: '各州差异明显，亲权保障需由当地律师逐案确认。'
  },
  {
    code: 'CY',
    country: '赛普勒斯',
    enName: 'Cyprus',
    flag: '🇨🇾',
    allowedCategories: ['spouses', 'single_woman', 'female_couple'],
    surrogacyAllowed: false,
    genderSelectionAllowed: true,
    eggDonationAllowed: true,
    spermDonationAllowed: true,
    averageCostUSD: 18000,
    quality: 4,
    summary: '常规 IVF、捐卵与双母试管资源成熟，费用低，但代孕保障不足。',
    legalNote: '适合作为 IVF/捐卵中心，不宜作为代孕亲权主路径。'
  },
  {
    code: 'TW',
    country: '台湾',
    enName: 'Taiwan',
    flag: '🇹🇼',
    allowedCategories: ['spouses'],
    surrogacyAllowed: false,
    genderSelectionAllowed: false,
    eggDonationAllowed: true,
    spermDonationAllowed: true,
    averageCostUSD: 14000,
    quality: 5,
    summary: '医疗技术成熟，但人工生殖准入主要限异性合法夫妻。',
    legalNote: '单身、代孕与多元家庭路径不开放。'
  },
  {
    code: 'TH',
    country: '泰国',
    enName: 'Thailand',
    flag: '🇹🇭',
    allowedCategories: ['spouses'],
    surrogacyAllowed: false,
    genderSelectionAllowed: true,
    eggDonationAllowed: true,
    spermDonationAllowed: true,
    averageCostUSD: 17500,
    quality: 4,
    summary: '亚洲深受信赖的国际生殖医疗枢纽，适合常规 IVF、PGT 与取卵冷冻。',
    legalNote: '外籍商业代孕不可作为泰国本地方案；配子与婚姻文件需合规。'
  },
  {
    code: 'MY',
    country: '马来西亚',
    enName: 'Malaysia',
    flag: '🇲🇾',
    allowedCategories: ['spouses'],
    surrogacyAllowed: false,
    genderSelectionAllowed: true,
    eggDonationAllowed: true,
    spermDonationAllowed: true,
    averageCostUSD: 15000,
    quality: 4,
    summary: '医疗机构配备成熟，中文/英文服务友好，适合常规 IVF 预算配置。',
    legalNote: '多元家庭与代孕准入需谨慎，通常不作为亲权保障主路径。'
  }
];

export const defaultTimeline: TimelinePhase[] = [
  {
    phase: '第一阶段',
    monthRange: '第 1 个月',
    title: '父亲路径咨询、精液评估与遗传建档',
    description: '建立准爸爸档案，完成精液分析、传染病检测、遗传带原筛查，并确认供卵与孕母筛查要求。'
  },
  {
    phase: '第二阶段',
    monthRange: '第 2 - 3 个月',
    title: '受精、胚胎多天期培育与 PGT-A 遗传学筛检分级',
    description: '依路径安排 ICSI/IVF、胚胎培养至囊胚阶段，并按合规地区进行筛检。'
  },
  {
    phase: '第三阶段',
    monthRange: '第 3 - 5 个月',
    title: '第三方配子或孕母匹配、律师函公证与托管信托设立',
    description: '完成供卵/供精/孕母资料审核、心理评估、合约与托管帐户安排。'
  },
  {
    phase: '第四阶段',
    monthRange: '第 6 - 14 个月',
    title: '移植、妊娠追踪、出生文件与返国证件',
    description: '依医嘱完成移植与产检，配合法律团队处理出生证明、亲权与旅行证件。'
  }
];
